/**
 * Weather Data Explorer Configuration
 *
 * Filters are managed by CBE's built-in filter system via quickFilters[].
 * The service adapter reads filter values from QueryParams.filters[].
 */
import { BaseExplorerConfig, QueryParams, QueryResult, FieldTransformer } from '../../../types/baseExplorerConfig';
import { appendWeatherFilters } from './weatherFilterParams';
import { WeatherDataRecord } from '../../../types/weatherData';
import { faCloudSun } from '@fortawesome/free-solid-svg-icons';
import { getBackendUrl, isDevMode } from '../../../services/configService';
import { getFWIDangerClass } from '../../../utils/fwiClassification';
import { pageToOffset, pageHasMore, backendPage } from '../../../utils/pagination';
import { WEATHER_COLUMNS } from '../../../shared/weatherSchema';
import { gridColumns } from '../../../shared/columnSchema';

const getAuthToken = (): string | null => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
        if (isDevMode()) {
            return 'dev-mode-token';
        } else {
            console.log('WeatherDataConfig: No access token found in production mode.');
            return null;
        }
    }
    return accessToken;
};

// ─── Filter option lists ──────────────────────────────────────────────────────

const REGION_OPTIONS = [
    { value: 'SS', label: 'South Slave' },
    { value: 'EV', label: 'Beaufort Delta' },
    { value: 'FS', label: 'Dehcho' },
    { value: 'ZF', label: 'North Slave' },
    { value: 'VQ', label: 'Sahtu' },
    { value: 'WBNP', label: 'Wood Buffalo National Park' }
];
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => ({
    value: i.toString(),
    label: `${i.toString().padStart(2, '0')}:00`,
}));
const DATA_SOURCE_OPTIONS = [
    { value: 'current', label: 'Current' },
    { value: 'historical', label: 'Historical' },
];
const STATUS_OPTIONS = [
    { value: 'valid', label: 'Valid' },
    { value: 'suspect', label: 'Suspect' },
    { value: 'invalid', label: 'Invalid' },
];

// ─── Features ─────────────────────────────────────────────────────────────────

const features = {
    pagination: {
        enabled: true,
        defaultPageSize: 100,
        pageSizeOptions: [25, 50, 100],
        showPageInfo: true,
    },
    sorting: {
        enabled: true,
        multiColumn: false,
        defaultSort: [{ field: 'timestamp', direction: 'desc' as const }],
    },
    filtering: {
        enabled: true,
        quickFilters: [
            {
                field: 'region',
                label: 'Region',
                type: 'dropdown' as const,
                options: REGION_OPTIONS,
            },
            {
                field: 'dateRange',
                label: 'Date Range',
                type: 'dateRange' as const,
            },
            {
                field: 'hour',
                label: 'Hour (UTC)',
                type: 'dropdown' as const,
                options: HOUR_OPTIONS,
            },
            {
                field: 'dataSource',
                label: 'Data Source',
                type: 'dropdown' as const,
                options: DATA_SOURCE_OPTIONS,
            },
            {
                field: 'status',
                label: 'Status',
                type: 'dropdown' as const,
                options: STATUS_OPTIONS,
            },
            {
                field: 'showInvalid',
                label: 'Include Invalid',
                type: 'dropdown' as const,
                options: [
                    { value: 'yes', label: 'Yes' },
                    { value: 'no', label: 'No' },
                ],
            },
            { field: 'stationId', label: 'Station ID', type: 'textSearch' as const },
            { field: 'stationName', label: 'Station Name', type: 'textSearch' as const },
            { field: 'temperature', label: 'Temperature', type: 'numeric' as const },
            { field: 'relativeHumidity', label: 'Humidity', type: 'numeric' as const },
            { field: 'windSpeed', label: 'Wind Speed', type: 'numeric' as const },
            { field: 'windDirection', label: 'Wind Direction', type: 'numeric' as const },
            { field: 'precipitation', label: 'Precipitation', type: 'numeric' as const },
            { field: 'fireWeatherIndex', label: 'Fire Weather Index', type: 'numeric' as const },
            { field: 'ffmc', label: 'FFMC', type: 'numeric' as const },
            { field: 'dmc', label: 'DMC', type: 'numeric' as const },
            { field: 'dc', label: 'DC', type: 'numeric' as const },
            { field: 'isi', label: 'ISI', type: 'numeric' as const },
            { field: 'bui', label: 'BUI', type: 'numeric' as const },
            { field: 'errors', label: 'Errors', type: 'textSearch' as const },
        ],
        advancedFilters: false,
    },
    searching: {
        enabled: true,
        placeholder: 'Search by station ID, name, or region...',
        searchFields: ['stationId', 'stationName', 'region'],
        debounceMs: 300,
    },
    export: {
        enabled: true,
        formats: ['csv'] as ('csv' | 'excel' | 'pdf')[],
        filename: 'weather-data',
        maxRecords: 1000,
        includeFilters: true,
    },
};

// ─── Presentation ─────────────────────────────────────────────────────────────

const presentation = {
    title: 'Weather Data',
    subtitle: 'Unified weather data from 2016 to present',
    icon: faCloudSun,
    columnConfig: gridColumns(WEATHER_COLUMNS),
    layout: { minHeight: 400, responsive: true, showToolbar: true, showFooter: true },
};

// ─── Exported config ──────────────────────────────────────────────────────────

// ─── Field Transformers ──────────────────────────────────────────────────────

const fwiTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (value == null || value === '') return '';
        const fwi = parseFloat(value);
        if (isNaN(fwi)) return String(value);
        // FWI is guaranteed numeric by the isNaN guard above, so the
        // classification is non-null. Non-null assert to keep the call site
        // tight per "no fallbacks for canonical data".
        const classification = getFWIDangerClass(fwi)!;
        return `<span class="fwi-badge ${classification.cssClass}">${fwi.toFixed(1)} (${classification.label})</span>`;
    },
};

// #547 — CFFDRS/FBP numeric formatting.
// Matt's spec: ISI to 1 decimal; FFMC/DMC/DC/BUI to integer.
// Papa's spec for FBP additions: ROS/HFI/CFB/CDSR to integer.
const oneDecimalTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (value == null || value === '') return '';
        const n = parseFloat(value);
        if (isNaN(n)) return String(value);
        return n.toFixed(1);
    },
};

const integerTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (value == null || value === '') return '';
        const n = parseFloat(value);
        if (isNaN(n)) return String(value);
        return Math.round(n).toString();
    },
};

const timestampTransformer: FieldTransformer = {
    transform: (value: any) => {
        if (value == null || value === '') return '';
        try {
            // Render in NWT local time so Date/Time and Hour columns agree
            // regardless of the user's browser timezone. en-CA + hour12:false
            // → "yyyy-mm-dd, HH:mm".
            return new Date(value).toLocaleString('en-CA', {
                timeZone: 'America/Yellowknife',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            });
        } catch {
            return String(value);
        }
    },
};

export const weatherDataConfig: BaseExplorerConfig<WeatherDataRecord> = {
    entityName: 'WeatherData',
    complexityLevel: 'moderate',
    autoRefreshIntervalMs: 300000,
    displayFields: ['stationId', 'timestamp', 'temperature', 'fireWeatherIndex', 'dataSource'],
    fieldTransformers: {
        fireWeatherIndex: fwiTransformer,
        timestamp: timestampTransformer,
        isi: oneDecimalTransformer,
        ffmc: integerTransformer,
        dmc: integerTransformer,
        dc: integerTransformer,
        bui: integerTransformer,
        ros: integerTransformer,
        hfi: integerTransformer,
        cfb: integerTransformer,
        cdsr: integerTransformer,
    } as any,
    serviceAdapter: {
        fetchData: async (params: QueryParams): Promise<QueryResult<WeatherDataRecord>> => {
            const { page, limit } = backendPage(params);
            const apiParams = new URLSearchParams({
                limit: limit.toString(),
                offset: pageToOffset(page, limit).toString(),
            });

            // Sort
            if (params.sort && params.sort.length > 0) {
                const sortParam = params.sort[0];
                apiParams.append('sortBy', sortParam.field);
                apiParams.append('sortOrder', sortParam.direction);
            }

            // Search (station ID)
            if (params.search?.term) {
                apiParams.append('search', params.search.term);
            }

            // Read filters from QueryParams.filters[] (set by CBE's filter system).
            // #693: shared mapping so the export (WeatherDataExplorer) forwards
            // the exact same constraints and the two can't drift.
            appendWeatherFilters(apiParams, params.filters);

            const backendUrl = getBackendUrl();
            const apiUrl = backendUrl
                ? `${backendUrl}/api/weather-data`
                : '/api/weather-data';

            const token = getAuthToken();
            const headers: HeadersInit = { 'Content-Type': 'application/json' };
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${apiUrl}?${apiParams}`, {
                method: 'GET',
                headers,
                credentials: 'include',
            });

            const result = await response.json();

            const dataWithIds = result.data.map((record: WeatherDataRecord, index: number) => ({
                ...record,
                id: record.id || `${record.stationId}-${record.timestamp || index}`,
            }));

            return {
                data: dataWithIds,
                total: result.pagination.total,
                page,
                limit,
                hasMore: pageHasMore(page, limit, result.pagination.total),
            };
        },
    },
    features,
    presentation,
};
